import React, { useState, useEffect } from 'react';
import { twoFAAPI } from '../services/api';

function Account({ user }) {
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);

  // Setup flow state
  const [setupStep, setSetupStep] = useState(null); // null | 'qr' | 'done'
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [setupError, setSetupError] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);

  // Disable flow state
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disableLoading, setDisableLoading] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    twoFAAPI.status()
      .then(res => setTotpEnabled(res.data.totp_enabled))
      .catch(() => {})
      .finally(() => setStatusLoading(false));
  }, []);

  const startSetup = async () => {
    setSetupError('');
    setSetupLoading(true);
    try {
      const res = await twoFAAPI.setup();
      setQrCode(res.data.qrCode);
      setSecret(res.data.secret);
      setSetupStep('qr');
    } catch {
      setSetupError('Failed to start setup. Please try again.');
    } finally {
      setSetupLoading(false);
    }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setSetupError('');
    setSetupLoading(true);
    try {
      await twoFAAPI.verifySetup({ secret, code: setupCode });
      setTotpEnabled(true);
      setSetupStep(null);
      setQrCode('');
      setSecret('');
      setSetupCode('');
      setMsg('Two-factor authentication enabled.');
      setTimeout(() => setMsg(''), 4000);
    } catch (err) {
      setSetupError(err.response?.data?.error || 'Invalid code — please try again.');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleDisable = async () => {
    setDisableLoading(true);
    try {
      await twoFAAPI.disable();
      setTotpEnabled(false);
      setShowDisableConfirm(false);
      setMsg('Two-factor authentication disabled.');
      setTimeout(() => setMsg(''), 4000);
    } catch {
      setMsg('Failed to disable 2FA. Please try again.');
    } finally {
      setDisableLoading(false);
    }
  };

  // Format secret into groups of 4 for readability
  const formatSecret = (s) => s ? s.match(/.{1,4}/g)?.join(' ') : '';

  return (
    <div className="container fade-in">
      <div className="page-header">
        <div>
          <h2>Account</h2>
          <p>Manage your account settings</p>
        </div>
      </div>

      {msg && (
        <div style={{
          marginBottom: '16px', padding: '12px 16px', borderRadius: '8px',
          background: 'rgba(34,197,94,0.1)', color: '#22c55e', fontWeight: 600, fontSize: '14px'
        }}>
          {msg}
        </div>
      )}

      {/* 2FA Section */}
      <div className="card" style={{ padding: '24px', maxWidth: '560px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px' }}>Two-Factor Authentication</h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-muted)' }}>
              Require a code from your authenticator app on login.
            </p>
          </div>
          {!statusLoading && (
            <span style={{
              fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '12px',
              background: totpEnabled ? 'rgba(34,197,94,0.12)' : 'rgba(100,100,100,0.12)',
              color: totpEnabled ? '#22c55e' : 'var(--text-muted)'
            }}>
              {totpEnabled ? 'Enabled' : 'Disabled'}
            </span>
          )}
        </div>

        {statusLoading ? (
          <div className="loading"><div className="spinner"></div></div>
        ) : totpEnabled ? (
          <>
            {!showDisableConfirm ? (
              <button
                onClick={() => setShowDisableConfirm(true)}
                className="btn btn-sm"
                style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}>
                Disable 2FA
              </button>
            ) : (
              <div style={{ padding: '14px', background: 'rgba(239,68,68,0.06)', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.2)' }}>
                <p style={{ margin: '0 0 12px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                  Are you sure? You will no longer need a code to sign in.
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={handleDisable} disabled={disableLoading} className="btn btn-sm"
                    style={{ background: '#ef4444', color: '#fff', border: 'none' }}>
                    {disableLoading ? 'Disabling...' : 'Yes, disable'}
                  </button>
                  <button onClick={() => setShowDisableConfirm(false)} className="btn btn-secondary btn-sm">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {setupStep === null && (
              <button onClick={startSetup} disabled={setupLoading} className="btn btn-primary btn-sm">
                {setupLoading ? 'Loading...' : 'Enable 2FA'}
              </button>
            )}

            {setupStep === 'qr' && (
              <div>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                  Scan this QR code with <strong>Google Authenticator</strong>, <strong>Authy</strong>, or any TOTP app.
                </p>
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <img src={qrCode} alt="2FA QR Code"
                    style={{ width: '200px', height: '200px', borderRadius: '8px', border: '4px solid white' }} />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 6px' }}>
                    Can't scan? Enter this key manually:
                  </p>
                  <code style={{
                    display: 'block', padding: '8px 12px', borderRadius: '6px',
                    background: 'var(--bg-secondary)', fontSize: '13px',
                    letterSpacing: '2px', wordBreak: 'break-all', color: 'var(--text-primary)'
                  }}>
                    {formatSecret(secret)}
                  </code>
                </div>

                <form onSubmit={confirmSetup}>
                  <div className="form-group">
                    <label className="form-label">Enter the 6-digit code to confirm</label>
                    <input
                      type="text" className="form-input"
                      value={setupCode} onChange={e => setSetupCode(e.target.value)}
                      placeholder="000 000" maxLength={7} autoComplete="one-time-code"
                      autoFocus required
                      style={{ textAlign: 'center', fontSize: '20px', letterSpacing: '6px', fontFamily: 'monospace' }}
                    />
                  </div>
                  {setupError && (
                    <div className="alert alert-error" style={{ marginBottom: '12px', fontSize: '14px' }}>
                      {setupError}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button type="submit" disabled={setupLoading} className="btn btn-primary btn-sm">
                      {setupLoading ? 'Verifying...' : 'Verify & Enable'}
                    </button>
                    <button type="button" onClick={() => { setSetupStep(null); setSetupCode(''); setSetupError(''); }}
                      className="btn btn-secondary btn-sm">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {setupError && setupStep === null && (
              <div className="alert alert-error" style={{ marginTop: '12px', fontSize: '14px' }}>{setupError}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Account;
